import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Users, Database, Shield, FileText, BookOpen, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import PremiumProducts from './admin/PremiumProducts';
import UserManagement from '@/components/admin/UserManagement';
import StripeCustomers from '@/components/admin/StripeCustomers';
import { SubscriptionManagement } from '@/components/admin/SubscriptionManagement';
import PremiumStatus from '@/pages/admin/PremiumStatus';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { SleepContentManagement } from '@/components/admin/SleepContentManagement';
import { UserAnalytics } from '@/components/admin/UserAnalytics';

/**
 * Configuration page - Admin only
 * Central hub for system configuration and administration
 */
export default function Configuration() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <AdminLayout activeTab="configuration">
      <div className="container mx-auto p-8 max-w-7xl">
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20">
                  <Settings className="h-6 w-6 text-primary" />
                </div>
                <h1 className="text-4xl font-bold tracking-tight">Configuration</h1>
                <Badge variant="default" className="bg-gradient-to-r from-primary to-primary/80 border-0 shadow-sm">
                  Admin
                </Badge>
              </div>
              <p className="text-base text-muted-foreground pl-14">
                System configuration and administration settings
              </p>
            </div>
          </div>

          {/* Admin Info Card */}
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-primary/3 to-transparent shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="p-1.5 rounded-lg bg-primary/10">
                  <Shield className="h-4 w-4 text-primary" />
                </div>
                Administrator Access
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              <p className="text-sm text-muted-foreground">
                Logged in as: <span className="font-semibold text-foreground">{user?.email}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Role: <span className="font-semibold text-foreground">
                  {user?.user_metadata?.roles?.join(', ') || user?.user_metadata?.role || 'Admin'}
                </span>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Configuration Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <div className="relative">
            {/* Left scroll arrow */}
            {showLeftArrow && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm shadow-md hover:bg-background"
                onClick={() => scroll('left')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            
            {/* Scrollable tabs container */}
            <div 
              ref={scrollContainerRef}
              onScroll={checkScroll}
              className="w-full overflow-x-auto scrollbar-hide"
            >
              <TabsList className="inline-flex w-auto min-w-full h-auto p-1.5 bg-muted/50 rounded-xl">
                <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm whitespace-nowrap">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm whitespace-nowrap">
                  Analytics
                </TabsTrigger>
                <TabsTrigger value="premium" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm whitespace-nowrap">
                  Premium
                </TabsTrigger>
                <TabsTrigger value="billing" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm whitespace-nowrap">
                  Billing
                </TabsTrigger>
                <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm whitespace-nowrap">
                  Users
                </TabsTrigger>
                <TabsTrigger value="sleep-content" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm whitespace-nowrap">
                  Sleep Content
                </TabsTrigger>
                <TabsTrigger value="database" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm whitespace-nowrap">
                  Database
                </TabsTrigger>
                <TabsTrigger value="system" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm whitespace-nowrap">
                  System
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Right scroll arrow */}
            {showRightArrow && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm shadow-md hover:bg-background"
                onClick={() => scroll('right')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card className="border-border/50 hover:border-border transition-colors hover:shadow-md group">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-base font-semibold mt-4">
                    User Management
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Manage user accounts, roles, and permissions
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border/50 hover:border-border transition-colors hover:shadow-md group">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors">
                      <Database className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-base font-semibold mt-4">
                    Database Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Configure reference tables and lookup values
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border/50 hover:border-border transition-colors hover:shadow-md group">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors">
                      <Settings className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-base font-semibold mt-4">
                    System Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Application settings and feature flags
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
                <CardDescription>Common administrative tasks</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-muted-foreground">
                  Configuration features will be added here as needed.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4">
            <UserAnalytics />
          </TabsContent>

        {/* Premium Tab */}
        <TabsContent value="premium" className="space-y-4">
          <PremiumProducts />
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-4">
          <PremiumStatus />
          <SubscriptionManagement />
          <StripeCustomers />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <UserManagement />
        </TabsContent>

        {/* Sleep Content Tab */}
        <TabsContent value="sleep-content" className="space-y-4">
          <SleepContentManagement />
        </TabsContent>

        {/* Database Tab */}
        <TabsContent value="database" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Database Configuration
              </CardTitle>
              <CardDescription>
                Configure reference tables and lookup values
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground">
                Database configuration interface coming soon...
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="system" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                System Settings
              </CardTitle>
              <CardDescription>
                Application-wide settings and configurations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground">
                System settings interface coming soon...
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </AdminLayout>
  );
}
